import React, { useState, MutableRefObject } from "react"
import styled from "styled-components/macro"
import { useForm, Controller, SubmitHandler } from "react-hook-form"
import alertActions from "lib/store/services/Alert/AlertSlice"
import { useAppDispatch } from "lib/store"
import { Common } from "@lib/types"
import { Outcomes, FrontendOutcome } from "@lib/types/discount"

import SingleSelect from "components/Ui/Form/SingleSelect"
import Input from "components/Ui/Form/Input"
import QuickAdd from "components/Ui/Table/QuickAdd"
import FixedDiscounts from "../../FixedDiscounts"

const ConditionsAdd = styled(QuickAdd)`
  border-top: 0.1rem solid ${(p) => p.theme.colors.greyLight};
  border-bottom: 0.1rem solid ${(p) => p.theme.colors.greyLight};
  padding: 2rem 0;
  display: block;
`

const Percentage = styled.div`
  width: 100%;
  flex: auto !important;
  margin-top: 2rem;
  display: flex;
`

type Inputs = {
  discountValueType: string
  valueType: string
  percentage: number
  currency: string
  amount: number
}

type Props = {
  outcome?: FrontendOutcome
  addOutcome: (data: Outcomes) => void
  currencyOptions: Common.Option[]
  submitRef: MutableRefObject<HTMLButtonElement>
}

export const CartDiscount = ({ outcome, addOutcome, currencyOptions, submitRef }: Props) => {
  const dispatch = useAppDispatch()
  const [fixedDiscounts, setFixedDiscounts] = useState<Common.Money[]>(outcome?.data?.discountValue?.fixedDiscounts ?? [])
  const [valueType, setValueType] = useState<string>(outcome?.data?.discountValue?.type ?? "")

  const {
    control,
    formState: { errors },
    handleSubmit
  } = useForm<Inputs>()

  const updateValueType = (valueType: Common.Option) => {
    setValueType(valueType.value)
  }

  const getOption = (type: string) => {
    switch (type) {
      case "RELATIVE":
        return {
          value: "RELATIVE",
          label: "Percentage"
        }
      case "FIXED":
        return {
          value: "FIXED",
          label: "Fixed amount"
        }
      default:
        return {
          label: "Select discount value type",
          value: ""
        }
    }
  }

  const onSubmit: SubmitHandler<Inputs> = (data) => {
    if (!valueType) {
      dispatch(
        alertActions.actions.createAlert({
          message: "Please select a discount value type",
          type: "error"
        })
      )
      return
    }
    if (valueType === "FIXED" && fixedDiscounts.length === 0) {
      dispatch(
        alertActions.actions.createAlert({
          message: "At least one fixed discount is required",
          type: "error"
        })
      )
      return
    }
    addOutcome({
      discountValue: {
        type: valueType,
        ...(valueType === "FIXED" && {
          fixedDiscounts: fixedDiscounts,
          percentage: undefined
        }),
        ...(valueType === "RELATIVE" && {
          percentage: data.percentage,
          fixedDiscounts: []
        })
      }
    })
  }

  return (
    <>
      <ConditionsAdd>
        <form>
          <Controller
            name="valueType"
            render={({ field }) => (
              <SingleSelect
                {...field}
                options={[getOption("FIXED"), getOption("RELATIVE")]}
                setValue={updateValueType}
                defaultValue={valueType ? getOption(valueType) : getOption("")}
              />
            )}
            control={control}
          />

          <>
            {valueType === "RELATIVE" && (
              <Percentage>
                <Controller
                  name="percentage"
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="number"
                      placeholder="Percentage, 1 - 100"
                      label="Percentage value *"
                      errors={errors}
                    />
                  )}
                  defaultValue={outcome?.data?.discountValue?.percentage ?? undefined}
                  control={control}
                  rules={{
                    required: "This is a required field",
                    pattern: {
                      value: /^\d+$/,
                      message: "Only integers 0 - 9 is allowed"
                    },
                    min: {
                      value: 1,
                      message: "Min value is 1"
                    },
                    max: { value: 100, message: "Max value is 100" }
                  }}
                />
              </Percentage>
            )}
            {valueType === "FIXED" && (
              <FixedDiscounts fixedDiscounts={fixedDiscounts} setFixedDiscounts={setFixedDiscounts} currencyOptions={currencyOptions} />
            )}
          </>
          <button onClick={handleSubmit(onSubmit)} ref={submitRef} type="submit" style={{ display: "none" }} />
        </form>
      </ConditionsAdd>
    </>
  )
}
